import { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, Loader2, Eye, Home, Plus, X, Youtube, Link as LinkIcon, Users, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminHomepage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  
  const [settings, setSettings] = useState({
    // Hero Section
    hero_title: 'Master in Engineering, JEE & NEET Concepts',
    hero_subtitle: 'Deep conceptual clarity + exam-oriented preparation through personalized 1:1 and group tutoring for Engineering, JEE, and NEET aspirants',
    
    // Features
    features: [
      { title: '1:1 Tutoring', description: 'Personalized attention for maximum conceptual clarity' },
      { title: 'Group Classes', description: 'Learn with peers in structured batch programs' },
      { title: 'JEE Preparation', description: 'Complete preparation for JEE Main & Advanced' },
      { title: 'NEET Preparation', description: 'Comprehensive NEET coaching' }
    ],
    
    // CTA Section
    cta_title: 'Ready to Master Engineering, JEE & NEET?',
    cta_subtitle: 'Join hundreds of students who have achieved their academic goals with Tutor Flex',
    
    // Contact Info
    contact_email: 'kaushaltutor@gmail.com',
    contact_phone: '9693339188',
    whatsapp_number: '7834909148',
    
    // Quick Links (for footer)
    quick_links: [
      { name: 'Home', url: '/' },
      { name: 'Courses', url: '/courses' },
      { name: 'Teachers', url: '/teachers' },
      { name: 'Contact', url: '/contact' }
    ],
    
    // Services (for footer)
    services: [
      '1:1 Tutoring',
      'Group Classes',
      'JEE Preparation',
      'NEET Preparation',
      'Engineering Subjects'
    ],
    
    // Demo Videos
    demo_videos: [],
    
    // Video Testimonials
    video_testimonials: [],
    
    // Social Links
    social_links: {
      youtube: '',
      instagram: '',
      facebook: '',
      telegram: ''
    },
    
    // Announcements/Banner
    announcement: {
      enabled: false,
      text: '',
      link: ''
    },
    
    // Footer tagline
    footer_tagline: 'Master Engineering, JEE & NEET Concepts with Expert Guidance'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/homepage-settings`, getAuthHeaders());
      if (response.data && Object.keys(response.data).length > 0) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/homepage-settings`, settings, getAuthHeaders());
      toast.success('Homepage settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for arrays
  const addItem = (field, defaultItem) => {
    setSettings(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), defaultItem]
    }));
  };

  const updateItem = (field, index, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeItem = (field, index) => {
    setSettings(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const tabs = [
    { id: 'hero', name: 'Hero Section', icon: Home },
    { id: 'features', name: 'Features', icon: Plus },
    { id: 'videos', name: 'Demo Videos', icon: Youtube },
    { id: 'testimonials', name: 'Video Testimonials', icon: MessageSquare },
    { id: 'footer', name: 'Footer & Links', icon: LinkIcon },
    { id: 'contact', name: 'Contact Info', icon: Users },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm text-center py-12">
        <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
        <p className="mt-4 text-slate-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-slate-200 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark flex items-center gap-2">
            <Home className="h-6 w-6" />
            Homepage Settings
          </h1>
          <p className="text-slate-600 text-sm mt-1">Customize your website content</p>
        </div>
        <div className="flex gap-2">
          <a href="/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <Eye className="h-4 w-4 mr-2" /> Preview
            </Button>
          </a>
          <Button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save All
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'border-brand-blue text-brand-blue' 
                : 'border-transparent text-slate-600 hover:text-brand-dark'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Hero Section */}
        {activeTab === 'hero' && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <Label>Hero Title</Label>
              <Input
                value={settings.hero_title}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_title: e.target.value }))}
                placeholder="Main headline"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Hero Subtitle</Label>
              <Textarea
                value={settings.hero_subtitle}
                onChange={(e) => setSettings(prev => ({ ...prev, hero_subtitle: e.target.value }))}
                placeholder="Supporting text"
                rows={3}
                className="mt-1"
              />
            </div>
            <div>
              <Label>CTA Section Title</Label>
              <Input
                value={settings.cta_title}
                onChange={(e) => setSettings(prev => ({ ...prev, cta_title: e.target.value }))}
                placeholder="Call to action title"
                className="mt-1"
              />
            </div>
            <div>
              <Label>CTA Section Subtitle</Label>
              <Textarea
                value={settings.cta_subtitle}
                onChange={(e) => setSettings(prev => ({ ...prev, cta_subtitle: e.target.value }))}
                rows={2}
                className="mt-1"
              />
            </div>
            
            {/* Announcement Banner */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Announcement Banner</Label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.announcement?.enabled || false}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      announcement: { ...prev.announcement, enabled: e.target.checked }
                    }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Enable</span>
                </label>
              </div>
              {settings.announcement?.enabled && (
                <div className="space-y-2">
                  <Input
                    value={settings.announcement?.text || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      announcement: { ...prev.announcement, text: e.target.value }
                    }))}
                    placeholder="Announcement text..."
                  />
                  <Input
                    value={settings.announcement?.link || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      announcement: { ...prev.announcement, link: e.target.value }
                    }))}
                    placeholder="Link (optional)"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Features */}
        {activeTab === 'features' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label className="text-lg">Homepage Features ({settings.features?.length || 0})</Label>
              <Button 
                onClick={() => addItem('features', { title: '', description: '' })} 
                variant="outline" 
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Feature
              </Button>
            </div>
            <div className="space-y-3">
              {(settings.features || []).map((feature, index) => (
                <div key={index} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <Input
                      value={feature.title}
                      onChange={(e) => updateItem('features', index, { ...feature, title: e.target.value })}
                      placeholder="Feature title"
                    />
                    <Input
                      value={feature.description}
                      onChange={(e) => updateItem('features', index, { ...feature, description: e.target.value })}
                      placeholder="Description"
                    />
                  </div>
                  <Button onClick={() => removeItem('features', index)} variant="ghost" size="sm" className="text-red-500">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Demo Videos */}
        {activeTab === 'videos' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <Label className="text-lg">Demo Videos</Label>
                <p className="text-sm text-slate-500">Add YouTube videos to showcase on homepage</p>
              </div>
              <Button 
                onClick={() => addItem('demo_videos', { title: '', youtube_url: '', description: '' })} 
                variant="outline" 
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Video
              </Button>
            </div>
            {(settings.demo_videos || []).length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Youtube className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No demo videos added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {settings.demo_videos.map((video, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                      <Input
                        value={video.title}
                        onChange={(e) => updateItem('demo_videos', index, { ...video, title: e.target.value })}
                        placeholder="Video title"
                      />
                      <Input
                        value={video.youtube_url}
                        onChange={(e) => updateItem('demo_videos', index, { ...video, youtube_url: e.target.value })}
                        placeholder="YouTube URL"
                      />
                      <div className="flex gap-2">
                        <Input
                          value={video.description}
                          onChange={(e) => updateItem('demo_videos', index, { ...video, description: e.target.value })}
                          placeholder="Description"
                          className="flex-1"
                        />
                        <Button onClick={() => removeItem('demo_videos', index)} variant="destructive" size="sm">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Video Testimonials */}
        {activeTab === 'testimonials' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <Label className="text-lg">Video Testimonials</Label>
                <p className="text-sm text-slate-500">Add student video testimonials (YouTube links)</p>
              </div>
              <Button 
                onClick={() => addItem('video_testimonials', { student_name: '', youtube_url: '', course: '' })} 
                variant="outline" 
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Video Testimonial
              </Button>
            </div>
            {(settings.video_testimonials || []).length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500">No video testimonials added yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {settings.video_testimonials.map((testimonial, index) => (
                  <div key={index} className="p-4 border border-slate-200 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Input
                        value={testimonial.student_name}
                        onChange={(e) => updateItem('video_testimonials', index, { ...testimonial, student_name: e.target.value })}
                        placeholder="Student name"
                      />
                      <Input
                        value={testimonial.youtube_url}
                        onChange={(e) => updateItem('video_testimonials', index, { ...testimonial, youtube_url: e.target.value })}
                        placeholder="YouTube URL"
                      />
                      <Input
                        value={testimonial.course}
                        onChange={(e) => updateItem('video_testimonials', index, { ...testimonial, course: e.target.value })}
                        placeholder="Course (JEE/NEET/etc)"
                      />
                      <Button onClick={() => removeItem('video_testimonials', index)} variant="destructive" size="sm">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer & Links */}
        {activeTab === 'footer' && (
          <div className="space-y-6">
            {/* Footer Tagline */}
            <div>
              <Label>Footer Tagline</Label>
              <Input
                value={settings.footer_tagline}
                onChange={(e) => setSettings(prev => ({ ...prev, footer_tagline: e.target.value }))}
                placeholder="Short description for footer"
                className="mt-1"
              />
            </div>

            {/* Quick Links */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <Label>Quick Links</Label>
                <Button 
                  onClick={() => addItem('quick_links', { name: '', url: '' })} 
                  variant="outline" 
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Link
                </Button>
              </div>
              <div className="space-y-2">
                {(settings.quick_links || []).map((link, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={link.name}
                      onChange={(e) => updateItem('quick_links', index, { ...link, name: e.target.value })}
                      placeholder="Link name"
                      className="flex-1"
                    />
                    <Input
                      value={link.url}
                      onChange={(e) => updateItem('quick_links', index, { ...link, url: e.target.value })}
                      placeholder="URL (e.g., /courses)"
                      className="flex-1"
                    />
                    <Button onClick={() => removeItem('quick_links', index)} variant="ghost" size="sm" className="text-red-500">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <Label>Services List</Label>
                <Button 
                  onClick={() => addItem('services', '')} 
                  variant="outline" 
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Service
                </Button>
              </div>
              <div className="space-y-2">
                {(settings.services || []).map((service, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={service}
                      onChange={(e) => {
                        const newServices = [...settings.services];
                        newServices[index] = e.target.value;
                        setSettings(prev => ({ ...prev, services: newServices }));
                      }}
                      placeholder="Service name"
                      className="flex-1"
                    />
                    <Button onClick={() => removeItem('services', index)} variant="ghost" size="sm" className="text-red-500">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Links */}
            <div className="border border-slate-200 rounded-lg p-4">
              <Label className="mb-3 block">Social Media Links</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-slate-500">YouTube Channel</Label>
                  <Input
                    value={settings.social_links?.youtube || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, youtube: e.target.value }
                    }))}
                    placeholder="https://youtube.com/..."
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Instagram</Label>
                  <Input
                    value={settings.social_links?.instagram || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, instagram: e.target.value }
                    }))}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Facebook</Label>
                  <Input
                    value={settings.social_links?.facebook || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, facebook: e.target.value }
                    }))}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-500">Telegram</Label>
                  <Input
                    value={settings.social_links?.telegram || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      social_links: { ...prev.social_links, telegram: e.target.value }
                    }))}
                    placeholder="https://t.me/..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact Info */}
        {activeTab === 'contact' && (
          <div className="space-y-4 max-w-md">
            <div>
              <Label>Email</Label>
              <Input
                value={settings.contact_email}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="contact@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={settings.contact_phone}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_phone: e.target.value }))}
                placeholder="+91 9876543210"
                className="mt-1"
              />
            </div>
            <div>
              <Label>WhatsApp Number</Label>
              <Input
                value={settings.whatsapp_number}
                onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                placeholder="+91 9876543210"
                className="mt-1"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
